import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { DropdownItem } from './DropdownItem';
import { DropdownSection } from './DropdownSection';

import { getFilterItemId, getSelectionId } from './filterItemIds';
import styles from './OrganizationDropdown.module.css';

const dedupeById = (items) =>
  items.filter((entry, index, arr) => arr.findIndex((x) => getSelectionId(x) === getSelectionId(entry)) === index);

const itemPropType = PropTypes.shape({
  key: PropTypes.string,
  title: PropTypes.string.isRequired,
  value: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  children: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string.isRequired,
      value: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
    }),
  ),
});

const sectionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  items: PropTypes.arrayOf(itemPropType).isRequired,
  showTrailingArrow: PropTypes.bool,
});

const SUB_CLOSE_MS = 140;

export const GroupedCheckboxFilterMenu = ({
  sections,
  selectedItems = [],
  onChange,
  fieldName,
  checkboxClassName,
}) => {
  const selected = useMemo(() => selectedItems || [], [selectedItems]);
  const [activeParentId, setActiveParentId] = useState(null);
  const closeTimerRef = useRef(null);

  const normalizedSections = useMemo(() => {
    const mapped = (sections || []).map((section) => ({
      ...section,
      showTrailingArrow: section.showTrailingArrow !== false,
    }));
    return mapped.filter((s) => Array.isArray(s.items) && s.items.length > 0);
  }, [sections]);

  const selectedIds = useMemo(() => new Set(selected.map((v) => getSelectionId(v))), [selected]);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleCloseSub = useCallback(() => {
    cancelCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setActiveParentId(null);
      closeTimerRef.current = null;
    }, SUB_CLOSE_MS);
  }, [cancelCloseTimer]);

  const activateSub = useCallback(
    (parentId) => {
      cancelCloseTimer();
      setActiveParentId(parentId);
    },
    [cancelCloseTimer],
  );

  const clearSub = useCallback(() => {
    cancelCloseTimer();
    setActiveParentId(null);
  }, [cancelCloseTimer]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const handleParentToggle = (parent) => ({ checked }) => {
    const children = parent?.children || [];
    const parentId = getFilterItemId(parent);

    if (checked) {
      onChange(dedupeById([...selected, parent.value, ...children.map((c) => c.value)]));
      if (children.length) activateSub(parentId);
      return;
    }

    const removeIds = new Set([parentId, ...children.map((c) => getFilterItemId(c))]);
    onChange(selected.filter((v) => !removeIds.has(getSelectionId(v))));
    if (activeParentId === parentId) setActiveParentId(null);
  };

  const handleChildToggle = (parent, child) => ({ checked }) => {
    const siblings = parent?.children || [];
    const childId = getFilterItemId(child);
    const parentId = getFilterItemId(parent);

    if (checked) {
      const withChild = dedupeById([...selected, child.value]);
      const allSiblingsIn = siblings.every((s) =>
        withChild.some((v) => getSelectionId(v) === getFilterItemId(s)),
      );
      onChange(allSiblingsIn ? dedupeById([...withChild, parent.value]) : withChild);
      return;
    }

    onChange(
      selected.filter((v) => getSelectionId(v) !== childId && getSelectionId(v) !== parentId),
    );
  };

  const parentVisualState = (parent) => {
    const children = parent?.children || [];
    const parentInSelected = selected.some((v) => getSelectionId(v) === getFilterItemId(parent));
    const selectedChildCount = children.filter((c) =>
      selected.some((v) => getSelectionId(v) === getFilterItemId(c)),
    ).length;
    const anyChildSelected = selectedChildCount > 0;
    const allChildrenSelected = children.length > 0 && selectedChildCount === children.length;
    // Show full checked state whenever the parent is selected, all clinics are selected,
    // or any clinic is selected (avoid indeterminate / “square” so parent matches child styling).
    const checked = parentInSelected || allChildrenSelected || (children.length > 0 && anyChildSelected);
    const indeterminate = false;
    return {
      checked,
      indeterminate,
      hasChildren: children.length > 0,
    };
  };

  return (
    <div className={styles.menuRoot}>
      <div className={styles.menuViewport}>
        <div className={styles.scrollArea}>
        {normalizedSections.map((section, sectionIndex) => (
          <DropdownSection
            key={section.id}
            title={section.title}
            showDividerBelow={sectionIndex < normalizedSections.length - 1}>
            <div className={styles.itemList}>
              {section.items.map((parent) => {
                const parentId = getFilterItemId(parent);
                const { checked, indeterminate, hasChildren } = parentVisualState(parent);

                return (
                  <DropdownItem
                    key={parentId}
                    parent={parent}
                    fieldName={fieldName}
                    checkboxClassName={checkboxClassName}
                    checked={checked}
                    indeterminate={indeterminate}
                    hasChildren={hasChildren}
                    showArrow={section.showTrailingArrow}
                    isSubOpen={activeParentId === parentId}
                    onParentCheckbox={handleParentToggle(parent)}
                    onChildCheckbox={handleChildToggle}
                    selectedIds={selectedIds}
                    onActivate={activateSub}
                    onScheduleClose={scheduleCloseSub}
                    onCancelClose={cancelCloseTimer}
                    onClearSub={clearSub}
                  />
                );
              })}
            </div>
          </DropdownSection>
        ))}
        </div>
      </div>
    </div>
  );
};

GroupedCheckboxFilterMenu.propTypes = {
  sections: PropTypes.arrayOf(sectionPropType).isRequired,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  onChange: PropTypes.func.isRequired,
  fieldName: PropTypes.string.isRequired,
  checkboxClassName: PropTypes.string,
};
